/**
 * @fileoverview Reducer para gestionar los mensajes de una conversación en el inbox.
 * @module reducers/inbox/conversationMessagesReducer
 * @category Reducer - Inbox
 * @since 1.0.0
 * @version 1.0.0
 */

import {
  CONVERSATION_MESSAGES_FAILURE,
  CONVERSATION_MESSAGES_LOADING,
  SET_CONVERSATION_MESSAGES_DATA,
  SET_CONVERSATION_MESSAGES_CURRENT_PAGE
} from "../../actions/inbox/conversationActions";
import { UPDATE_OR_ADD_MESSAGE } from "../../actions/inbox/messagesOptimizedActions";

const initialState = {
  loading: false,
  data: [], // Mensajes de la conversación
  messageIndexMap: {},
  pagination: {
    current_page: 1,
    has_next_page: false
  },
  error: null
};

/**
 * @param {*} raw
 * @returns {string|null} ID externo normalizado o null si no aplica deduplicación
 */
function normalizeExternalMessageId(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw);
}

/** @deprecated alias — usar normalizeExternalMessageId */
function normalizeAccountUid(raw) {
  return normalizeExternalMessageId(raw);
}

/**
 * Evita duplicados visibles al listar mensajes (WhatsApp: account_uid; Messenger/IG: mid).
 * Conserva el primero en orden del array; omite filas posteriores con el mismo identificador externo.
 *
 * @param {Array} messages
 * @returns {Array}
 */
function dedupeMessagesForDisplay(messages) {
  const seenAccountUid = {};
  const seenMid = {};
  const out = [];

  for (let i = 0; i < messages.length; i += 1) {
    const msg = messages[i];
    const accountUid = normalizeExternalMessageId(msg.account_uid);
    const mid = normalizeExternalMessageId(msg.mid);

    const duplicateAccountUid = accountUid != null && seenAccountUid[accountUid];
    const duplicateMid = mid != null && seenMid[mid];

    if (!duplicateAccountUid && !duplicateMid) {
      if (accountUid != null) seenAccountUid[accountUid] = true;
      if (mid != null) seenMid[mid] = true;
      out.push(msg);
    }
  }

  return out;
}

function addMessageIndexes(messageIndexMap, message, index) {
  [message.identifier, message.id].forEach((id) => {
    if (id != null && String(id).trim() !== '') {
      // eslint-disable-next-line no-param-reassign
      messageIndexMap[String(id)] = index;
    }
  });
}

function buildMessageIndexMap(messages) {
  const messageIndexMap = {};
  messages.forEach((message, index) => {
    addMessageIndexes(messageIndexMap, message, index);
  });
  return messageIndexMap;
}

/**
 * Reducer para los mensajes de una conversación específica.
 *
 * Maneja los estados de loading, éxito y error para la acción fetchConversationMessages.
 *
 * @function conversationMessagesReducer
 * @param {Object} state - Estado actual
 * @param {Object} action - Acción despachada
 * @returns {Object} Nuevo estado
 */
export default function conversationMessagesReducer(state = initialState, action) {
  switch (action.type) {
    case CONVERSATION_MESSAGES_LOADING:
      return {
        ...state,
        loading: true,
        error: null
      };
    case SET_CONVERSATION_MESSAGES_DATA: {
      const newData = action.payload.data || [];
      const currentPage = action.payload?.pagination?.page || 1;

      const mergedData = currentPage > 1 ? [...newData, ...state.data] : newData;
      const data = dedupeMessagesForDisplay(mergedData);
      const messageIndexMap = buildMessageIndexMap(data);

      return {
        ...state,
        loading: false,
        data,
        messageIndexMap,
        pagination: {
          current_page: currentPage,
          has_next_page: action.payload?.pagination?.next > currentPage
        },
        error: null
      };
    }
    case CONVERSATION_MESSAGES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error
      };
    case SET_CONVERSATION_MESSAGES_CURRENT_PAGE: {
      const currentPage = action.payload;
      return {
        ...state,
        pagination: {
          ...state.pagination,
          current_page: currentPage
        }
      };
    }
    case UPDATE_OR_ADD_MESSAGE: {
      const newMessage = action.payload?.message;
      const insertIfMissing = action.payload?.insertIfMissing !== false;

      if (!newMessage) return state;

      const incomingMid = normalizeExternalMessageId(newMessage.mid);
      if (incomingMid != null) {
        const existingMidIndex = state.data.findIndex(
          (msg) => normalizeExternalMessageId(msg.mid) === incomingMid
        );

        if (existingMidIndex !== -1) {
          const newData = [...state.data];
          newData[existingMidIndex] = {
            ...newData[existingMidIndex],
            ...newMessage
          };

          return {
            ...state,
            data: newData,
            messageIndexMap: buildMessageIndexMap(newData),
            loading: false,
            error: null
          };
        }
      }

      const incomingUid = normalizeAccountUid(newMessage.account_uid);

      let newData;
      let newMap;

      if (incomingUid != null) {
        const id = newMessage.identifier || newMessage.id;
        const key = id ? String(id) : null;

        const existingUidIndex = state.data.findIndex(
          (msg) => normalizeAccountUid(msg.account_uid) === incomingUid
        );

        if (existingUidIndex !== -1) {
          const existing = state.data[existingUidIndex];
          const existingId = existing.identifier || existing.id;
          const sameIdentity = Boolean(
            key
            && existingId != null
            && String(existingId).trim() !== ""
            && String(existingId) === key
          );

          if (!sameIdentity) {
            return state;
          }

          newData = [...state.data];
          newData[existingUidIndex] = {
            ...newData[existingUidIndex],
            ...newMessage
          };
          newMap = buildMessageIndexMap(newData);
        } else {
          newData = [...state.data];
          newMap = { ...state.messageIndexMap };

          if (key && newMap[key] !== undefined) {
            const existingIndex = newMap[key];
            newData[existingIndex] = {
              ...newData[existingIndex],
              ...newMessage
            };
            addMessageIndexes(newMap, newData[existingIndex], existingIndex);
          } else {
            if (!insertIfMissing) return state;
            newData.push(newMessage);
            addMessageIndexes(newMap, newMessage, newData.length - 1);
          }
        }
      } else {
        const id = newMessage.identifier || newMessage.id;
        const key = id ? String(id) : null;

        newData = [...state.data];
        newMap = { ...state.messageIndexMap };

        if (key && newMap[key] !== undefined) {
          const existingIndex = newMap[key];

          newData[existingIndex] = {
            ...newData[existingIndex],
            ...newMessage
          };
          addMessageIndexes(newMap, newData[existingIndex], existingIndex);
        } else {
          if (!insertIfMissing) return state;
          newData.push(newMessage);
          addMessageIndexes(newMap, newMessage, newData.length - 1);
        }
      }

      return {
        ...state,
        data: newData,
        messageIndexMap: newMap,
        loading: false,
        error: null
      };
    }
    default:
      return state;
  }
}
